import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { CaptchaSlot } from 'armxlib'

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string
  remove(widget: string): void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let script: Promise<TurnstileApi> | undefined

function loadTurnstile(): Promise<TurnstileApi> {
  script ??= new Promise((resolve, reject) => {
    const element = document.createElement('script')
    element.src = SCRIPT_URL
    element.async = true
    element.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not load')))
    element.onerror = () => {
      script = undefined
      reject(new Error('Turnstile did not load'))
    }
    document.head.append(element)
  })
  return script
}

/** Props of {@link Captcha}. */
export interface CaptchaProps {
  /** Called with a token once the check passes, and with `null` when it expires. */
  onToken: (token: string | null) => void
  /** Called when the widget cannot load or the check fails for good. */
  onError: () => void
}

/** A Cloudflare Turnstile widget, managed mode, dark theme, in the library's `CaptchaSlot`. */
export function Captcha({ onToken, onError }: CaptchaProps) {
  const container = useRef<HTMLDivElement>(null)
  const token = useEffectEvent(onToken)
  const error = useEffectEvent(onError)

  useEffect(() => {
    let widget: string | undefined
    let cancelled = false
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !container.current) return
        widget = turnstile.render(container.current, {
          sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
          theme: 'dark',
          size: 'flexible',
          callback: (value: string) => token(value),
          'expired-callback': () => token(null),
          'error-callback': () => error(),
        })
      })
      .catch(() => error())
    return () => {
      cancelled = true
      if (widget) window.turnstile?.remove(widget)
    }
  }, [])

  return (
    <CaptchaSlot size="flexible">
      <div ref={container} />
    </CaptchaSlot>
  )
}

/** A captcha for a form, and its token. */
export interface CaptchaState {
  /** Token to send with the form; `null` until the check passes. */
  token: string | null
  /** The widget could not load or the check failed for good. */
  failed: boolean
  /** Asks for a new check: a token is good for one request only. */
  reset: () => void
  /** The widget, to place in the form. */
  widget: ReactNode
}

/**
 * A Turnstile widget and its token. {@link CaptchaState.reset} mounts a new
 * widget, which runs a new check.
 * @returns The token and the widget.
 */
export function useCaptcha(): CaptchaState {
  const [token, setToken] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  const reset = useCallback(() => {
    setToken(null)
    setFailed(false)
    setAttempt((count) => count + 1)
  }, [])

  const widget = (
    <Captcha
      key={attempt}
      onToken={(value) => {
        setFailed(false)
        setToken(value)
      }}
      onError={() => setFailed(true)}
    />
  )
  return { token, failed, reset, widget }
}
